/**
 * SharedRevealPage - Pre-signature shared summary view
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowRight, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SharedRevealPage = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notReady, setNotReady] = useState(false);

  useEffect(() => {
    fetchSharedSummary();
  }, []);

  const fetchSharedSummary = async () => {
    try {
      const relationshipId = sessionStorage.getItem('relationship_id');
      const response = await axios.get(`${API}/responses/shared/${relationshipId}`);
      setSummary(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shared summary:', error);
      if (error.response?.status === 403) {
        setNotReady(true);
      } else {
        toast.error('Failed to load shared summary');
      }
      setLoading(false);
    }
  };

  const handleProceedToSign = () => {
    navigate('/signature');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center" style={{minHeight: 'calc(100vh - 100px)'}}>
          <Loader2 className="w-8 h-8 animate-spin text-stone-900" />
        </div>
      </div>
    );
  }

  if (notReady) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
          <div className="max-w-lg w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-3xl font-bold text-stone-900 mb-4">Not Ready Yet</h2>
            <p className="text-stone-600 mb-8">
              Both partners must lock their responses before viewing the shared summary.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-stone-400 mx-auto mb-4" />
            <p className="text-stone-600">Summary not available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-stone-900 mb-4">Shared Summary</h2>
            <p className="text-lg text-stone-600">
              Both of you have locked your responses. Review this summary before signing.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-8 mb-8">
            <h3 className="text-2xl font-bold text-stone-900 mb-6">Response Overview</h3>
            <p className="text-stone-600 mb-6">
              Your individual responses are now visible to each other. Take time to review before proceeding to sign.
            </p>
            
            <div className="bg-stone-50 rounded-xl p-6 mb-6">
              <p className="text-sm text-stone-600">
                <strong>Note:</strong> Signing your MarrsingClause® is a symbolic commitment. 
                After both partners sign, a detailed comparison will be generated along with your official certificate.
              </p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={handleProceedToSign}
              className="bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-12 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center gap-2"
              data-testid="proceed-to-sign"
            >
              Proceed to Sign
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedRevealPage;
